'use client';

import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for responsive media query detection
 *
 * @param query - CSS media query string
 * @returns boolean indicating if the media query matches
 *
 * @example
 * ```tsx
 * const isDesktop = useMediaQuery('(min-width: 1024px)');
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * ```
 */
export function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState<boolean>(() => {
        // SSR safe: default to false on server
        if (typeof window === 'undefined') {
            return false;
        }
        return window.matchMedia(query).matches;
    });

    const handleChange = useCallback((event: MediaQueryListEvent) => {
        setMatches(event.matches);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const mediaQueryList = window.matchMedia(query);

        // Set initial value
        setMatches(mediaQueryList.matches);

        // Add listener (use addEventListener for modern browsers)
        if (mediaQueryList.addEventListener) {
            mediaQueryList.addEventListener('change', handleChange);
        } else {
            // Fallback for older browsers
            mediaQueryList.addListener(handleChange);
        }

        return () => {
            if (mediaQueryList.removeEventListener) {
                mediaQueryList.removeEventListener('change', handleChange);
            } else {
                // Fallback for older browsers
                mediaQueryList.removeListener(handleChange);
            }
        };
    }, [query, handleChange]);

    return matches;
}

/**
 * Predefined breakpoints for common use cases
 */
export const BREAKPOINTS = {
    mobile: '(max-width: 767px)',
    tablet: '(min-width: 768px) and (max-width: 1023px)',
    desktop: '(min-width: 1024px)',
    largeDesktop: '(min-width: 1280px)',
} as const;

/**
 * Hook to detect if current viewport is desktop
 * @param breakpoint - Custom breakpoint (default: 1024px)
 * @returns boolean indicating if viewport is desktop size
 */
export function useIsDesktop(breakpoint = 1024): boolean {
    return useMediaQuery(`(min-width: ${breakpoint}px)`);
}

/**
 * Hook to detect if current viewport is mobile
 * @param breakpoint - Custom breakpoint (default: 768px)
 * @returns boolean indicating if viewport is mobile size
 */
export function useIsMobile(breakpoint = 768): boolean {
    return useMediaQuery(`(max-width: ${breakpoint - 1}px)`);
}

export default useMediaQuery;
